import { EnumerationsClaim } from 'client/identity'
import type { ComputedRef } from 'vue'
import { useAuthStore } from '~/stores/auth'

export function useUserClaims(): Record<string, ComputedRef<boolean>> {
  const AuthStore = useAuthStore()
  const userClaims = computed(() => {
    return AuthStore.user?.claims ?? []
  })

  const hasClaim = (claim: EnumerationsClaim) =>
    computed(() => userClaims.value.includes(claim))

  // System Permissions
  const hasReadWriteUserAdministration = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.WriteUserAdministration)
  })

  const hasReadUserAdministration = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.ReadUserAdministration)
  })

  const hasReadWriteTenantAdministration = computed(() => {
    return userClaims.value.includes(
      EnumerationsClaim.WriteTenantAdministration
    )
  })

  const hasReadWriteDocumentCenterAdministration = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.WriteDocumentCenter)
  })

  const hasReadDocumentCenterAdministration = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.ReadDocumentCenter)
  })

  const hasReadWriteClientSettings = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.WriteClientSettings)
  })

  const hasReadClientSettings = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.ReadClientSettings)
  })

  const hasUserAdministrationSystemPermission = computed(() => {
    return (
      hasReadWriteUserAdministration.value || hasReadUserAdministration.value
    )
  })

  const hasTenantAdministrationSystemPermission = computed(() => {
    return hasReadWriteTenantAdministration.value
  })

  const hasDocumentCenterSystemPermission = computed(() => {
    return (
      hasReadWriteDocumentCenterAdministration.value ||
      hasReadDocumentCenterAdministration.value
    )
  })

  const hasClientSettingsSystemPermission = computed(() => {
    return hasReadWriteClientSettings.value || hasReadClientSettings.value
  })

  // Service Function Permissions

  const hasReadWriteDashboardServiceFunctionPermission = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.WriteDataViz)
  })

  const hasReadDashboardServiceFunctionPermission = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.ReadDataViz)
  })

  const hasReadWriteInsightsServiceFunctionPermissions = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.WriteInsights)
  })

  const hasReadInsightsServiceFunctionPermissions = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.ReadInsights)
  })

  const hasReadWriteReportsServiceFunctionPermission = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.WriteReports)
  })

  const hasReadReportsServiceFunctionPermission = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.ReadReports)
  })

  const hasReadWriteCommunicationServiceFunctionPermission = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.WriteCommunication)
  })

  const hasReadCommunicationServiceFunctionPermission = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.ReadCommunication)
  })

  const hasReadWriteDocumentsDeliverablesServiceFunctionPermission = computed(
    () => {
      return userClaims.value.includes(EnumerationsClaim.WriteDeliverables)
    }
  )

  const hasReadDocumentsDeliverablesServiceFunctionPermission = computed(() => {
    return userClaims.value.includes(EnumerationsClaim.ReadDeliverables)
  })

  const hasReadResourceLibraryPermission = computed(() =>
    userClaims.value.includes(EnumerationsClaim.ReadResourceLibrary)
  )

  const shouldSeeDashboard = computed(() => {
    return hasReadDashboardServiceFunctionPermission.value
  })

  const shouldSeeInsights = computed(() => {
    return hasReadInsightsServiceFunctionPermissions.value
  })

  const shouldSeeReports = computed(() => {
    return hasReadReportsServiceFunctionPermission.value
  })

  const shouldSeeCommunication = computed(() => {
    return hasReadCommunicationServiceFunctionPermission.value
  })

  const shouldSeeDocumentsAndDeliverables = computed(() => {
    return hasReadDocumentsDeliverablesServiceFunctionPermission.value
  })

  // TODO: refactor this to match the other claims computeds
  const supportAccessPermissions = {
    hasReadWriteClientSupportAccessPermission: hasClaim(
      EnumerationsClaim.ReadWriteClientSupportAccess
    ),
    hasReadWriteAdminSupportAccessPermission: hasClaim(
      EnumerationsClaim.ReadWriteAdminSupportAccess
    ),
    shouldSeeSupportAccess: computed(() => {
      return (
        hasClaim(EnumerationsClaim.ReadWriteClientSupportAccess).value ||
        hasClaim(EnumerationsClaim.ReadWriteAdminSupportAccess).value
      )
    }),
  }

  const shouldSeeViewAs = computed(() =>
    userClaims.value.includes(EnumerationsClaim.ViewAs)
  )

  return {
    // System Permissions
    // For control of page-level access/display
    shouldSeeClientManagement: hasTenantAdministrationSystemPermission,
    shouldSeeUserManagement: hasUserAdministrationSystemPermission,
    shouldSeeDocumentCenter: hasDocumentCenterSystemPermission,
    shouldSeeClientSettings: hasClientSettingsSystemPermission,
    shouldSeeResourceLibrary: hasReadResourceLibraryPermission,
    shouldSeeViewAs,

    shouldSeeDashboard,
    shouldSeeInsights,
    shouldSeeReports,
    shouldSeeCommunication,
    shouldSeeDocumentsAndDeliverables,

    // For control of displayed data within pages
    hasReadWriteClientSettings,
    hasReadClientSettings,
    hasReadWriteDocumentCenterAdministration,
    hasReadDocumentCenterAdministration,
    hasReadWriteTenantAdministration,
    hasReadWriteUserAdministration,
    hasReadUserAdministration,

    // Service Function Permissions
    hasReadWriteDashboardServiceFunctionPermission,
    hasReadDashboardServiceFunctionPermission,
    hasReadWriteInsightsServiceFunctionPermissions,
    hasReadInsightsServiceFunctionPermissions,
    hasReadWriteReportsServiceFunctionPermission,
    hasReadReportsServiceFunctionPermission,
    hasReadWriteCommunicationServiceFunctionPermission,
    hasReadCommunicationServiceFunctionPermission,
    hasReadWriteDocumentsDeliverablesServiceFunctionPermission,
    hasReadDocumentsDeliverablesServiceFunctionPermission,

    ...supportAccessPermissions,
  }
}
